import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useAccount } from '../../context/AccountContext';
import { GET_TINYCMS_PAGE, GET_TINYCMS_PAGES, RUN_TINYCMS_PAGE_COPY_EDIT } from '../../graphql';
import {
  ApprovalHistory,
  articleStatus,
  Finding,
  FindingsList,
  ItemState,
  PreviewButton,
  PublishPanel,
  StatusDot,
} from './shared';

/**
 * Site → Pages. Pages (Home, Pricing, a docs group) are site structure built
 * from blocks — the agent writes and edits them over MCP. Here a member sees
 * every page, what is live, its checks, a preview, and publishes or takes one
 * down; their click is the approval for the exact version shown.
 */

interface PageRow {
  id: string;
  path: string;
  title: string;
  workingHash: string;
  state: ItemState & { live: (ItemState['live'] & { path?: string }) | null };
  blockCount?: number;
}

const BLOCK_LABEL: Record<string, string> = {
  hero: 'Hero',
  text: 'Text',
  features: 'Features',
  cta: 'Call to action',
  image: 'Image',
  quote: 'Quote',
  faq: 'FAQ',
  articles: 'Article list',
  article: 'Featured article',
};

export function CmsPages({ siteUrl }: { siteUrl?: string | null }) {
  const { accountId } = useAccount();
  const [open, setOpen] = useState<string | null>(null);
  const { data, loading, refetch } = useQuery(GET_TINYCMS_PAGES, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const pages: PageRow[] = data?.tinycmsPages || [];

  if (loading && !pages.length) return <p className="ink-site-row-desc">Loading…</p>;
  if (!pages.length) {
    return (
      <div className="ink-site-card">
        <div className="ink-site-row">
          <div>
            <div className="ink-site-row-title">No pages yet</div>
            <div className="ink-site-row-desc">
              Ask your agent for one — “Create a pricing page”, or “Make a home page with a hero, three
              features and our latest articles”. It appears here for you to preview and publish.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ink-site-card">
      {pages.map((p) => {
        const status = articleStatus(p.state);
        const nested = p.path.split('/').length > 2;
        const livePath = p.state.live?.path;
        return (
          <div key={p.id}>
            <div className="ink-site-row" style={nested ? { paddingLeft: 20 } : undefined}>
              <div style={{ minWidth: 0 }}>
                <div className="ink-site-row-title">{p.title}</div>
                <div className="ink-site-row-desc">
                  {livePath && siteUrl ? (
                    <a href={`${siteUrl}${livePath}`} target="_blank" rel="noreferrer">
                      {p.path} ↗
                    </a>
                  ) : (
                    p.path
                  )}
                  {typeof p.blockCount === 'number' ? ` · ${p.blockCount} blocks` : ''}
                </div>
              </div>
              <span className="ink-row-actions">
                <StatusDot tone={status.tone} label={status.label} />
                <button
                  type="button"
                  className="ink-btn ink-btn-quiet"
                  onClick={() => setOpen(open === p.id ? null : p.id)}
                  aria-expanded={open === p.id}
                >
                  {open === p.id ? 'Close' : 'Review'}
                </button>
              </span>
            </div>
            {open === p.id ? <PageDetail id={p.id} onChanged={() => void refetch()} /> : null}
          </div>
        );
      })}
    </div>
  );
}

function PageDetail({ id, onChanged }: { id: string; onChanged: () => void }) {
  const { accountId } = useAccount();
  const { data, loading, refetch } = useQuery(GET_TINYCMS_PAGE, {
    variables: { accountId, id },
    skip: !accountId,
    fetchPolicy: 'network-only',
  });
  const [copyEdit, { loading: editing }] = useMutation(RUN_TINYCMS_PAGE_COPY_EDIT);
  const [error, setError] = useState('');
  const page = data?.tinycmsPage;
  if (loading && !page) return <p className="ink-site-row-desc" style={{ padding: '0 0 16px' }}>Loading…</p>;
  if (!page) return null;

  const state = page.state;
  const preflight: Finding[] = state.preflight || [];
  const copy: Finding[] = state.copyEdit?.fresh ? state.copyEdit.findings || [] : [];
  const notRun = !state.copyEdit?.fresh;
  const findings = [...preflight, ...(state.copyEdit?.required ? copy : copy.map((f) => ({ ...f, severity: 'warning' as const })))];
  const blocking =
    findings.filter((f) => f.severity === 'blocking').length + (notRun && state.copyEdit?.required ? 1 : 0);
  const blocks: Array<{ type: string; heading?: string }> = page.content?.blocks || [];

  const runCopyEdit = async () => {
    setError('');
    try {
      await copyEdit({ variables: { accountId, id } });
      await refetch();
      onChanged();
    } catch (err: any) {
      setError(err.message);
    }
  };

  return (
    <div style={{ padding: '4px 0 20px', display: 'grid', gap: 18 }}>
      <div>
        <p className="ink-site-kicker">Blocks</p>
        <ol style={{ margin: 0, paddingLeft: 18, font: '400 13px/1.6 var(--ink-font)', color: 'var(--ink-ink2)' }}>
          {blocks.map((b, i) => (
            <li key={i}>
              {BLOCK_LABEL[b.type] || b.type}
              {b.heading ? <span style={{ color: 'var(--ink-ink3)' }}> — {b.heading}</span> : null}
            </li>
          ))}
        </ol>
        {page.children?.length ? (
          <p className="ink-site-row-desc">
            Group: {page.children.map((c: { path: string }) => c.path).join(', ')}
          </p>
        ) : null}
        <p className="ink-site-row-desc">Your agent edits pages. Ask it for any change.</p>
      </div>

      <div>
        <p className="ink-site-kicker">Checks</p>
        {notRun ? (
          <p className="ink-site-row-desc" style={{ marginBottom: 8 }}>
            The copy edit hasn’t run on this version{state.copyEdit?.required ? ' — it’s required to publish' : ''}.
          </p>
        ) : null}
        <FindingsList findings={findings} empty="No findings." />
        <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" className="ink-btn ink-btn-quiet" onClick={() => void runCopyEdit()} disabled={editing}>
            {editing ? 'Checking…' : 'Run copy edit'}
          </button>
          <PreviewButton pageId={id} label="Preview" />
        </div>
        {error ? <p className="text-xs text-error" style={{ marginTop: 6 }}>{error}</p> : null}
      </div>

      <div>
        <p className="ink-site-kicker">Publish</p>
        <PublishPanel
          kind="page"
          id={id}
          workingHash={page.workingHash}
          state={state}
          blocking={blocking}
          onDone={() => {
            void refetch();
            onChanged();
          }}
        />
      </div>

      <div>
        <p className="ink-site-kicker">History</p>
        <ApprovalHistory kind="page" subjectId={id} limit={10} />
      </div>
    </div>
  );
}
